import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, ShieldCheck } from "lucide-react";
import type { CaptchaChallengeResponse } from "@packages/global";

interface AuthCaptchaProps {
  value: string;
  error?: string;
  refreshSignal?: number;
  getCaptcha: () => Promise<CaptchaChallengeResponse>;
  onChange: (value: string) => void;
  onChallengeChange: (captchaId: string) => void;
  label: string;
  placeholder: string;
  refreshLabel: string;
}

const AuthCaptcha: React.FC<AuthCaptchaProps> = ({
  value,
  error,
  refreshSignal,
  getCaptcha,
  onChange,
  onChallengeChange,
  label,
  placeholder,
  refreshLabel,
}) => {
  const [imageDataUrl, setImageDataUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const lastRefreshSignalRef = useRef<number | undefined>();

  const loadCaptcha = useCallback(async () => {
    setLoading(true);
    try {
      const challenge = await getCaptcha();
      setImageDataUrl(challenge.imageDataUrl);
      onChallengeChange(challenge.captchaId);
      onChange("");
    } finally {
      setLoading(false);
    }
  }, [getCaptcha, onChallengeChange, onChange]);

  useEffect(() => {
    if (hasLoadedRef.current && lastRefreshSignalRef.current === refreshSignal) {
      return;
    }
    hasLoadedRef.current = true;
    lastRefreshSignalRef.current = refreshSignal;
    void loadCaptcha();
  }, [loadCaptcha, refreshSignal]);

  return (
    <div className="space-y-2">
      <label htmlFor="captchaCode" className="block text-sm font-medium text-text-primary">
        {label}
      </label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <ShieldCheck className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            id="captchaCode"
            type="text"
            autoComplete="off"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            className={`h-11 w-full rounded-lg border bg-bg-form pl-11 pr-4 text-sm uppercase text-text-primary placeholder:normal-case placeholder:text-text-muted transition-colors focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/10 ${
              error ? "border-danger" : "border-border-default"
            }`}
          />
        </div>
        <button
          type="button"
          onClick={loadCaptcha}
          disabled={loading}
          className="flex h-11 w-[128px] shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border-default bg-bg-form text-sm font-medium text-text-primary transition-colors hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={refreshLabel}
        >
          {imageDataUrl && !loading ? (
            <img src={imageDataUrl} alt={refreshLabel} className="h-full w-full object-cover" />
          ) : (
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          )}
        </button>
      </div>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
};

export default AuthCaptcha;
